import React, { useEffect, useMemo, useState } from 'react';
import { MultiSelect, Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { getEstablishmentList } from '../../../slices/establishment/thunk';
import { getResidueCategoryList } from '../../../slices/residueCategory/thunk';
import { getResidueList } from '../../../slices/residue/thunk';
import { getStatusGuide } from '../../../slices/guide/thunk';
import { getFinalDestinationList } from '../../../slices/finalDestination/thunk';
import { getWarehouseList } from '../../../slices/warehouse/thunk';

const selectFilterStyles = {
	root: { width: '100%' },
	wrapper: {
		borderBottom: '1px solid #dee2e6',
		width: '100%',
	},
};

/** Origen: Mantine Select + lista al montar (mismo criterio que Estado). */
export function EstablishmentNameFilter({ column }) {
	const dispatch = useDispatch();
	const value = column.getFilterValue();
	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await dispatch(
					getEstablishmentList({ limit: 2000 }),
				).unwrap();
				if (cancelled || !list) return;
				const names = [
					...new Set(list.map(e => e.name).filter(Boolean)),
				].sort((a, b) => a.localeCompare(b, 'es'));
				setData(names.map(n => ({ value: n, label: n })));
			} catch {
				if (!cancelled) setData([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	return (
		<Select
			placeholder="Origen"
			clearable
			searchable
			data={data}
			value={value || null}
			onChange={v => column.setFilterValue(v ?? undefined)}
			size="xs"
			variant="unstyled"
			styles={selectFilterStyles}
		/>
	);
}

/** Residuo (ítem maestro): nombres desde GET residues, no tipos de residuo. */
export function ResidueNameFilter({ column }) {
	const dispatch = useDispatch();
	const value = column.getFilterValue();
	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await dispatch(
					getResidueList({ limit: 8000 }),
				).unwrap();
				if (cancelled || !list) return;
				const byId = new Map();
				(list || []).forEach(r => {
					if (r?._id == null) return;
					const id = String(r._id);
					if (!byId.has(id)) byId.set(id, r);
				});
				const rows = [...byId.values()].sort((a, b) =>
					String(a.name || '').localeCompare(String(b.name || ''), 'es'),
				);
				setData(
					rows.map(r => ({
						value: String(r._id),
						label: r.name || String(r._id),
					})),
				);
			} catch {
				if (!cancelled) setData([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	return (
		<Select
			placeholder="Residuo"
			clearable
			searchable
			data={data}
			value={value || null}
			onChange={v => column.setFilterValue(v ?? undefined)}
			size="xs"
			variant="unstyled"
			styles={selectFilterStyles}
		/>
	);
}

/**
 * Destino (FD o bodega): opciones combinadas; la API recibe $or vía normalización en index (campo destino_pick).
 */
export function DestinationNameFilter({ column }) {
	const dispatch = useDispatch();
	const value = column.getFilterValue();
	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const [fds, whs] = await Promise.all([
					dispatch(getFinalDestinationList()).unwrap(),
					dispatch(getWarehouseList()).unwrap(),
				]);
				if (cancelled) return;
				const names = new Set();
				(fds || []).forEach(d => {
					if (d?.name) names.add(d.name);
				});
				(whs || []).forEach(w => {
					if (w?.name) names.add(w.name);
				});
				const sorted = [...names].sort((a, b) => a.localeCompare(b, 'es'));
				setData(sorted.map(n => ({ value: n, label: n })));
			} catch {
				if (!cancelled) setData([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	return (
		<Select
			placeholder="Destino"
			clearable
			searchable
			data={data}
			value={value || null}
			onChange={v => column.setFilterValue(v ?? undefined)}
			size="xs"
			variant="unstyled"
			styles={selectFilterStyles}
		/>
	);
}

/** Estado de guía por nombre (status_guide_name). */
export function StatusGuideNameFilter({ column }) {
	const dispatch = useDispatch();
	const statusGuideList = useSelector(s => s.Guides.statusGuideList);
	const value = column.getFilterValue();

	useEffect(() => {
		dispatch(getStatusGuide());
	}, [dispatch]);

	const data = useMemo(
		() =>
			(statusGuideList || []).map(s => ({
				value: s.name,
				label: s.name,
			})),
		[statusGuideList],
	);

	return (
		<Select
			placeholder="Estado"
			clearable
			searchable
			data={data}
			value={value || null}
			onChange={v => column.setFilterValue(v ?? undefined)}
			size="xs"
			variant="unstyled"
			styles={selectFilterStyles}
		/>
	);
}

/** Categoría por id (multi; OData $in sobre residue_category_id). Etiqueta = nombre; valor único evita keys duplicadas en MultiSelect. */
export function ResidueCategoryNamesFilter({ column }) {
	const dispatch = useDispatch();
	const value = column.getFilterValue() || [];
	const [data, setData] = useState([]);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// El thunk no reenvía params al API; debe invocarse sin argumentos.
				const list = await dispatch(getResidueCategoryList()).unwrap();
				if (cancelled) return;
				const byId = new Map();
				(list || []).forEach(c => {
					if (c?._id == null) return;
					const id = String(c._id);
					if (!byId.has(id)) byId.set(id, c.name || id);
				});
				const opts = [...byId.entries()]
					.map(([value, label]) => ({ value, label }))
					.sort((a, b) =>
						String(a.label).localeCompare(String(b.label), 'es'),
					);
				setData(opts);
			} catch {
				if (!cancelled) setData([]);
			} finally {
				if (!cancelled) setLoaded(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	return (
		<MultiSelect
			placeholder={loaded ? 'Categorías' : 'Cargando…'}
			clearable
			searchable
			data={data}
			value={Array.isArray(value) ? value : []}
			onChange={v => column.setFilterValue(v.length ? v : undefined)}
			size="xs"
			variant="unstyled"
			styles={selectFilterStyles}
		/>
	);
}
